import React, {
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  MouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { STAGE_CONFIGS, StageConfigsMap } from "../types/board.types";
import {
  stageAutomationButtons,
  AutomationAction,
  SubTask,
  StageAutomationButtonsMap,
} from "../types/automation.types";
import { axiosInstance } from "../services/api";
import { ensureUserData, canDeleteIdea } from "../utils/authHelper";
import { isAuthenticated } from "../utils/auth";
import { authFetch } from "../utils/authFetch";
import { safeArrayToString, safeStringToArray } from "../utils/dataHelpers";
import AIExpansionDisplay from "./AIExpansionDisplay";

// Stage type
type StageName =
  | "Ideation"
  | "Research"
  | "PoC"
  | "Marketing"
  | "Sales"
  | "Validated";

// Idea interface matching PropTypes
interface Idea {
  id?: string | number;
  title?: string;
  description?: string;
  priority?: string;
  tags?: string | string[];
  stage?: string;
  owner_id?: string | number;
  problem?: string;
  audience?: string;
  solution?: string;
  primaryKPI?: string;
  assumptions?: string | string[];
  hypotheses?: string | string[];
  successCriteria?: string;
  competitors?: string | string[];
  marketNotes?: string;
  evidence?: string;
  pocScope?: string;
  criticalFlow?: string;
  acceptanceCriteria?: string;
  notes?: string;
  positioning?: string;
  primaryCTA?: string;
  targetSegments?: string | string[];
  icp?: string;
  offer?: string;
  successMetric?: string;
  metricsSummary?: string;
  decision?: string;
  rationale?: string;
}

interface StageFormProps {
  idea?: Idea | null;
  onSave: (data: ProcessedFormData) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

interface FormData {
  title: string;
  description: string;
  priority: string;
  tags: string;
  stage: string;
  problem: string;
  audience: string;
  solution: string;
  primaryKPI: string;
  assumptions: string;
  hypotheses: string;
  successCriteria: string;
  competitors: string;
  marketNotes: string;
  evidence: string;
  pocScope: string;
  criticalFlow: string;
  acceptanceCriteria: string;
  notes: string;
  positioning: string;
  primaryCTA: string;
  targetSegments: string;
  icp: string;
  offer: string;
  successMetric: string;
  metricsSummary: string;
  decision: string;
  rationale: string;
  [key: string]: string;
}

interface ProcessedFormData {
  title: string;
  description: string;
  priority: string;
  tags: string[];
  stage: string;
  problem: string;
  audience: string;
  solution: string;
  primaryKPI: string;
  assumptions: string[];
  hypotheses: string[];
  successCriteria: string;
  competitors: string[];
  marketNotes: string;
  evidence: string;
  pocScope: string;
  criticalFlow: string;
  acceptanceCriteria: string;
  notes: string;
  positioning: string;
  primaryCTA: string;
  targetSegments: string[];
  icp: string;
  offer: string;
  successMetric: string;
  metricsSummary: string;
  decision: string;
  rationale: string;
}

interface FormErrors {
  [key: string]: string;
}

interface AILoadingState {
  [key: string]: boolean;
}

interface UserLike {
  id: string | number;
  role?: string;
}

interface AutoFillResponse {
  success?: boolean;
  suggestion?: string;
}

interface ExpansionStatusResponse {
  success: boolean;
  data: {
    hasExpansions: boolean;
    expansionCount: number;
  };
}

interface ExpandResponse {
  success: boolean;
  error?: string;
  message?: string;
}

// SubTask, AutomationAction, StageConfig imported from types

const StageForm: React.FC<StageFormProps> = ({
  idea,
  onSave,
  onCancel,
  onDelete,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);

  console.log(
    "StageForm: Component rendering/re-rendering with idea:",
    idea?.id,
    idea?.title,
  );

  const [formData, setFormData] = useState<FormData>({} as FormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const [aiLoading, setAiLoading] = useState<AILoadingState>({});
  const [basicInfoExpanded, setBasicInfoExpanded] = useState<boolean>(true);
  const [automationExpanded, setAutomationExpanded] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState<string>("");
  const [deleteError, setDeleteError] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Get current user info with fallback
  const currentUser = ensureUserData() as UserLike | null;

  // Debug logging
  console.log("Current user:", currentUser);
  console.log("Idea being edited:", idea);
  console.log(
    "Owner ID comparison:",
    idea?.owner_id,
    "===",
    currentUser?.id,
    "=",
    idea?.owner_id === currentUser?.id,
  );
  console.log(
    "Is admin:",
    currentUser?.role === "application_admin" ||
      currentUser?.role === "organization_admin",
  );
  console.log(
    "User ID type:",
    typeof currentUser?.id,
    "Idea owner ID type:",
    typeof idea?.owner_id,
  );
  console.log("Can delete (owner):", idea?.owner_id === currentUser?.id);
  console.log(
    "Can delete (admin):",
    currentUser?.role === "application_admin" ||
      currentUser?.role === "organization_admin",
  );
  console.log(
    "Final can delete:",
    idea &&
      (idea.owner_id === currentUser?.id ||
        currentUser?.role === "application_admin" ||
        currentUser?.role === "organization_admin"),
  );

  const stages: StageName[] = [
    "Ideation",
    "Research",
    "PoC",
    "Marketing",
    "Sales",
    "Validated",
  ];

  useEffect(() => {
    // Auto-resize all textareas after content is loaded
    const resizeAllTextareas = () => {
      const textareas = document.querySelectorAll(
        ".modal textarea",
      ) as NodeListOf<HTMLTextAreaElement>;
      textareas.forEach((textarea) => {
        textarea.style.height = "auto";
        textarea.style.height = Math.max(60, textarea.scrollHeight) + "px";
      });
    };

    // Delay to ensure DOM is updated
    setTimeout(resizeAllTextareas, 100);
  }, [formData]);

  useEffect(() => {
    // Initialize form data with existing idea data or defaults for new idea
    if (idea) {
      // Debug logging for idea data
      console.log("StageForm: Initializing form with idea data:", {
        id: idea.id,
        title: idea.title,
        tags: idea.tags,
        tagsType: typeof idea.tags,
        isArray: Array.isArray(idea.tags),
        assumptions: idea.assumptions,
        assumptionsType: typeof idea.assumptions,
        isAssumptionsArray: Array.isArray(idea.assumptions),
      });
      setFormData({
        title: idea.title || "",
        description: idea.description || "",
        priority: idea.priority || "medium",
        tags: safeArrayToString(idea.tags),
        stage: idea.stage || "Ideation",
        // Stage-specific fields
        problem: idea.problem || "",
        audience: idea.audience || "",
        solution: idea.solution || "",
        primaryKPI: idea.primaryKPI || "",
        assumptions: safeArrayToString(idea.assumptions),
        hypotheses: safeArrayToString(idea.hypotheses),
        successCriteria: idea.successCriteria || "",
        competitors: safeArrayToString(idea.competitors),
        marketNotes: idea.marketNotes || "",
        evidence: idea.evidence || "",
        pocScope: idea.pocScope || "",
        criticalFlow: idea.criticalFlow || "",
        acceptanceCriteria: idea.acceptanceCriteria || "",
        notes: idea.notes || "",
        positioning: idea.positioning || "",
        primaryCTA: idea.primaryCTA || "",
        targetSegments: safeArrayToString(idea.targetSegments),
        icp: idea.icp || "",
        offer: idea.offer || "",
        successMetric: idea.successMetric || "",
        metricsSummary: idea.metricsSummary || "",
        decision: idea.decision || "",
        rationale: idea.rationale || "",
      });

      // Only expand the current stage for existing ideas
      setExpandedStages(new Set([idea.stage || "Ideation"]));
      setBasicInfoExpanded(false); // Collapse basic info for existing ideas
    } else {
      // Default values for new idea
      setFormData({
        title: "",
        description: "",
        priority: "medium",
        tags: "",
        stage: "Ideation",
        // Stage-specific fields
        problem: "",
        audience: "",
        solution: "",
        primaryKPI: "",
        assumptions: "",
        hypotheses: "",
        successCriteria: "",
        competitors: "",
        marketNotes: "",
        evidence: "",
        pocScope: "",
        criticalFlow: "",
        acceptanceCriteria: "",
        notes: "",
        positioning: "",
        primaryCTA: "",
        targetSegments: "",
        icp: "",
        offer: "",
        successMetric: "",
        metricsSummary: "",
        decision: "",
        rationale: "",
      });

      // For new ideas, start with basic info expanded and no stages expanded
      setExpandedStages(new Set());
      setBasicInfoExpanded(true);
    }
  }, [idea]);

  const handleInputChange = (field: string, value: string): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Auto-collapse basic info when user starts filling stage-specific fields
    const stageFields = [
      "problem",
      "audience",
      "solution",
      "primaryKPI",
      "assumptions",
      "hypotheses",
      "successCriteria",
      "competitors",
      "marketNotes",
      "evidence",
      "pocScope",
      "criticalFlow",
      "acceptanceCriteria",
      "notes",
      "positioning",
      "primaryCTA",
      "targetSegments",
      "icp",
      "offer",
      "successMetric",
      "metricsSummary",
      "decision",
      "rationale",
    ];

    if (
      stageFields.includes(field) &&
      value.trim() !== "" &&
      basicInfoExpanded
    ) {
      // Only auto-collapse if no stage is currently expanded
      if (expandedStages.size === 0) {
        setBasicInfoExpanded(false);
      }
    }
  };

  const handleTextareaResize = (
    e: ChangeEvent<HTMLTextAreaElement> | React.FormEvent<HTMLTextAreaElement>,
  ): void => {
    const textarea = e.target as HTMLTextAreaElement;
    textarea.style.height = "auto";
    textarea.style.height = Math.max(60, textarea.scrollHeight) + "px";
  };

  const handleAIFill = async (
    fieldName: string,
    label: string,
  ): Promise<void> => {
    setAiLoading((prev) => ({ ...prev, [fieldName]: true }));

    try {
      // Prepare context from other filled fields
      const context = {
        title: formData.title,
        description: formData.description,
        stage: formData.stage,
        problem: formData.problem,
        audience: formData.audience,
        solution: formData.solution,
        primaryKPI: formData.primaryKPI,
        assumptions: formData.assumptions,
        hypotheses: formData.hypotheses,
        successCriteria: formData.successCriteria,
        competitors: formData.competitors,
        marketNotes: formData.marketNotes,
        evidence: formData.evidence,
        pocScope: formData.pocScope,
        criticalFlow: formData.criticalFlow,
        acceptanceCriteria: formData.acceptanceCriteria,
        notes: formData.notes,
        positioning: formData.positioning,
        primaryCTA: formData.primaryCTA,
        targetSegments: formData.targetSegments,
        icp: formData.icp,
        offer: formData.offer,
        successMetric: formData.successMetric,
        metricsSummary: formData.metricsSummary,
        decision: formData.decision,
        rationale: formData.rationale,
      };

      // Check if user is authenticated
      if (!isAuthenticated()) {
        throw new Error(t("stages.form.automation.loginRequired"));
      }

      const response = await axiosInstance.post("/openai/autofill", {
        fieldName,
        label,
        context,
        currentValue: formData[fieldName] || "",
      });

      const data = response.data as AutoFillResponse;

      if (data.success && data.suggestion) {
        setFormData((prev) => ({
          ...prev,
          [fieldName]: data.suggestion as string,
        }));
      }
    } catch (error) {
      console.error("AI fill error:", error);

      // Show user-friendly error message
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("stages.form.automation.aiFillFailed");
      alert(t("stages.form.automation.aiFillError", { message: errorMessage }));
    } finally {
      setAiLoading((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const toggleStage = (stage: string): void => {
    setExpandedStages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stage)) {
        newSet.delete(stage);
      } else {
        // Close all other sections when opening a new one
        newSet.clear();
        newSet.add(stage);
      }
      return newSet;
    });

    // Also collapse basic info when expanding a stage
    if (!expandedStages.has(stage)) {
      setBasicInfoExpanded(false);
    }
  };

  const toggleBasicInfo = (): void => {
    setBasicInfoExpanded(!basicInfoExpanded);

    // Close all stage sections when opening basic info
    if (!basicInfoExpanded) {
      setExpandedStages(new Set());
    }
  };

  const toggleAutomation = (): void => {
    setAutomationExpanded(!automationExpanded);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const currentStage = formData.stage || "Ideation";
    const stageConfig = (STAGE_CONFIGS as StageConfigsMap)[currentStage];

    // Validate required fields for current stage
    stageConfig.requiredFields.forEach((field: string) => {
      if (!formData[field] || formData[field].trim() === "") {
        newErrors[field] = t("stages.form.validation.fieldRequired", {
          field: field.charAt(0).toUpperCase() + field.slice(1),
        });
      }
    });

    // Validate base required fields
    if (!formData.title || formData.title.trim() === "") {
      newErrors.title = t("stages.form.validation.titleRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error field
      const firstErrorField = document.querySelector(
        ".form-group.has-error input, .form-group.has-error textarea, .form-group.has-error select",
      ) as HTMLElement | null;
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        firstErrorField.focus();
      }
      return;
    }

    // Convert comma-separated strings back to arrays
    const processedData: ProcessedFormData = {
      ...formData,
      tags: safeStringToArray(formData.tags),
      assumptions: safeStringToArray(formData.assumptions),
      hypotheses: safeStringToArray(formData.hypotheses),
      competitors: safeStringToArray(formData.competitors),
      targetSegments: safeStringToArray(formData.targetSegments),
    };

    onSave(processedData);
  };

  const handleDeleteClick = (): void => {
    setShowDeleteConfirm(true);
    setDeleteConfirmText("");
    setDeleteError("");
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!idea?.title || deleteConfirmText.trim() !== idea.title) {
      setDeleteError(t("stages.form.deleteConfirm.typeTitleError"));
      return;
    }

    setIsDeleting(true);
    setDeleteError("");

    try {
      await axiosInstance.delete(`/kanban/ideas/${idea.id}`);
      onCancel(); // Close modal after successful deletion
      if (onDelete) {
        onDelete(); // Call the refresh callback
      }
    } catch (error) {
      console.error("Error deleting idea:", error);
      const axiosError = error as {
        response?: { data?: { error?: string; message?: string } };
      };
      setDeleteError(
        axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          t("stages.form.deleteConfirm.failed"),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = (): void => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText("");
    setDeleteError("");
  };

  const handleAutomationClick = async (
    stage: string,
    actionLabel: string,
  ): Promise<void> => {
    console.log("StageForm: Automation button clicked:", {
      stage,
      actionLabel,
    });

    // Handle AI expansion specifically
    if (actionLabel === "Generate AI Expansion") {
      console.log(
        "StageForm: Handling AI expansion request for idea:",
        idea?.id,
      );
      if (!idea?.id) {
        alert(t("stages.form.automation.saveFirst"));
        return;
      }

      // Check if expansions already exist
      try {
        const statusResponse = await authFetch(
          `/api/kanban/ideas/${idea.id}/expansion-status`,
        );
        const statusData =
          (await statusResponse.json()) as ExpansionStatusResponse;

        if (statusData.success && statusData.data.hasExpansions) {
          const confirmRegenerate = window.confirm(
            t("stages.form.automation.expansionRegenerateConfirm", {
              count: statusData.data.expansionCount,
            }),
          );
          if (!confirmRegenerate) {
            return;
          }
        }
      } catch (error) {
        console.error("Error checking expansion status:", error);
      }

      try {
        setAiLoading((prev) => ({ ...prev, "ai-expand": true }));

        const response = await authFetch(
          `/api/kanban/ideas/${idea.id}/expand`,
          {
            method: "POST",
          },
        );

        const data = (await response.json()) as ExpandResponse;

        if (data.success) {
          alert(t("stages.form.automation.expansionStarted"));

          // Poll for completion without refreshing the page
          let attempts = 0;
          const maxAttempts = 30; // 30 attempts * 3 seconds = 90 seconds max

          const pollForCompletion = async (): Promise<void> => {
            console.log(
              `Polling for AI expansion completion (attempt ${attempts + 1}/${maxAttempts})`,
            );

            try {
              const statusResponse = await authFetch(
                `/api/kanban/ideas/${idea.id}/expansion-status`,
              );
              const statusData =
                (await statusResponse.json()) as ExpansionStatusResponse;

              console.log("Expansion status:", statusData);

              if (
                statusData.success &&
                statusData.data.hasExpansions &&
                statusData.data.expansionCount >= 4
              ) {
                console.log(
                  "All AI expansions completed! Refreshing expansion display...",
                );

                // Instead of reloading the page, just trigger a re-render of the AI expansion component
                // We can do this by dispatching a custom event or using a callback
                const event = new CustomEvent("aiExpansionComplete", {
                  detail: { ideaId: idea.id },
                });
                window.dispatchEvent(event);

                return;
              }

              attempts++;
              if (attempts < maxAttempts) {
                console.log(
                  `Expansions not ready yet (${statusData.data?.expansionCount || 0}/4). Checking again in 3 seconds...`,
                );
                setTimeout(pollForCompletion, 3000); // Check every 3 seconds
              } else {
                console.log("Polling timeout reached. Stopping polling.");
                alert(t("stages.form.automation.expansionTimeout"));
              }
            } catch (error) {
              console.error("Error polling for completion:", error);
              attempts++;
              if (attempts < maxAttempts) {
                setTimeout(pollForCompletion, 3000);
              }
            }
          };

          // Start polling after 5 seconds
          console.log("Starting AI expansion polling in 5 seconds...");
          setTimeout(pollForCompletion, 5000);
        } else {
          alert(
            t("stages.form.automation.expansionFailed") +
              ": " +
              (data.error || data.message),
          );
        }
      } catch (error) {
        console.error("Error triggering AI expansion:", error);
        alert(t("stages.form.automation.expansionFailed"));
      } finally {
        setAiLoading((prev) => ({ ...prev, "ai-expand": false }));
      }
      return;
    }

    // Show a placeholder modal for other automation actions
    alert(
      t("stages.form.automation.automationTriggered", {
        stage,
        action: actionLabel,
      }),
    );
  };

  // Helper function to render select options based on field name
  const renderSelectOptions = (fieldName: string): React.ReactNode => {
    switch (fieldName) {
      case "priority":
        return (
          <>
            <option value="low">{t("stages.form.priority.low")}</option>
            <option value="medium">{t("stages.form.priority.medium")}</option>
            <option value="high">{t("stages.form.priority.high")}</option>
            <option value="critical">
              {t("stages.form.priority.critical")}
            </option>
          </>
        );
      case "decision":
        return (
          <>
            <option value="proceed">{t("stages.form.decision.proceed")}</option>
            <option value="iterate">{t("stages.form.decision.iterate")}</option>
            <option value="stop">{t("stages.form.decision.stop")}</option>
          </>
        );
      case "stage":
        return stages.map((stage) => (
          <option key={stage} value={stage}>
            {stage}
          </option>
        ));
      default:
        return null;
    }
  };

  const renderField = (
    fieldName: string,
    label: string,
    type: string = "text",
    required: boolean = false,
  ): React.ReactNode => {
    const currentStage = formData.stage || "Ideation";
    const stageConfig = (STAGE_CONFIGS as StageConfigsMap)[currentStage];
    const isRequired =
      required || stageConfig?.requiredFields?.includes(fieldName);

    // Show AI button for all text/textarea fields except title and stage
    // This makes it universal for any future fields added to the modal
    const showAIButton =
      (type === "text" || type === "textarea") &&
      fieldName !== "title" &&
      fieldName !== "stage";

    return (
      <div className={`form-group ${errors[fieldName] ? "has-error" : ""}`}>
        <div className="field-header">
          <label className="form-label">
            {label}
            {isRequired && <span style={{ color: "red" }}> *</span>}
          </label>
          {showAIButton && (
            <button
              type="button"
              className="ai-fill-btn"
              onClick={() => handleAIFill(fieldName, label)}
              disabled={aiLoading[fieldName]}
              title={t("stages.form.buttons.aiFill")}
            >
              {aiLoading[fieldName] ? (
                <span className="ai-loading ai-icon">&#9889;</span>
              ) : (
                <span className="ai-icon">&#9889;</span>
              )}
            </button>
          )}
        </div>
        {type === "textarea" ? (
          <div className="input-container">
            <textarea
              className={`form-input ${errors[fieldName] ? "error" : ""} ${aiLoading[fieldName] ? "ai-loading-field" : ""}`}
              value={formData[fieldName] || ""}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                handleInputChange(fieldName, e.target.value);
                handleTextareaResize(e);
              }}
              onInput={handleTextareaResize}
              rows={3}
              placeholder={t("stages.form.placeholders.enterField", {
                field: label.toLowerCase(),
              })}
              disabled={aiLoading[fieldName]}
              style={{ minHeight: "60px", height: "auto", overflow: "hidden" }}
            />
            {aiLoading[fieldName] && (
              <div className="field-spinner">
                <div className="spinner-small"></div>
              </div>
            )}
          </div>
        ) : type === "select" ? (
          <select
            className={`form-input ${errors[fieldName] ? "error" : ""}`}
            value={formData[fieldName] || ""}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              handleInputChange(fieldName, e.target.value)
            }
          >
            <option value="">
              {t("stages.form.placeholders.enterField", {
                field: label.toLowerCase(),
              })}
            </option>
            {renderSelectOptions(fieldName)}
          </select>
        ) : (
          <div className="input-container">
            <input
              type={type}
              className={`form-input ${errors[fieldName] ? "error" : ""} ${aiLoading[fieldName] ? "ai-loading-field" : ""}`}
              value={formData[fieldName] || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleInputChange(fieldName, e.target.value)
              }
              placeholder={t("stages.form.placeholders.enterField", {
                field: label.toLowerCase(),
              })}
              disabled={aiLoading[fieldName]}
            />
            {aiLoading[fieldName] && (
              <div className="field-spinner">
                <div className="spinner-small"></div>
              </div>
            )}
          </div>
        )}
        {errors[fieldName] && (
          <div
            className="error-message"
            style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
          >
            {errors[fieldName]}
          </div>
        )}
      </div>
    );
  };

  const renderStageFields = (stage: string): React.ReactNode => {
    switch (stage) {
      case "Ideation":
        return (
          <>
            {renderField(
              "problem",
              t("stages.form.fields.problem"),
              "textarea",
              true,
            )}
            {renderField(
              "audience",
              t("stages.form.fields.audience"),
              "textarea",
              true,
            )}
            {renderField(
              "solution",
              t("stages.form.fields.solution"),
              "textarea",
              true,
            )}
            {renderField("primaryKPI", t("stages.form.fields.primaryKPI"))}
            {renderField("assumptions", t("stages.form.fields.assumptions"))}
          </>
        );

      case "Research":
        return (
          <>
            {renderField("hypotheses", t("stages.form.fields.hypotheses"))}
            {renderField(
              "successCriteria",
              t("stages.form.fields.successCriteria"),
              "textarea",
            )}
            {renderField("competitors", t("stages.form.fields.competitors"))}
            {renderField(
              "marketNotes",
              t("stages.form.fields.marketNotes"),
              "textarea",
            )}
            {renderField(
              "evidence",
              t("stages.form.fields.evidence"),
              "textarea",
            )}
          </>
        );

      case "PoC":
        return (
          <>
            {renderField(
              "pocScope",
              t("stages.form.fields.pocScope"),
              "textarea",
            )}
            {renderField(
              "criticalFlow",
              t("stages.form.fields.criticalFlow"),
              "textarea",
            )}
            {renderField(
              "acceptanceCriteria",
              t("stages.form.fields.acceptanceCriteria"),
              "textarea",
            )}
            {renderField("notes", t("stages.form.fields.notes"), "textarea")}
          </>
        );

      case "Marketing":
        return (
          <>
            {renderField(
              "positioning",
              t("stages.form.fields.positioning"),
              "textarea",
            )}
            {renderField("primaryCTA", t("stages.form.fields.primaryCTA"))}
            {renderField(
              "targetSegments",
              t("stages.form.fields.targetSegments"),
            )}
          </>
        );

      case "Sales":
        return (
          <>
            {renderField("icp", t("stages.form.fields.icp"), "textarea")}
            {renderField("offer", t("stages.form.fields.offer"), "textarea")}
            {renderField(
              "successMetric",
              t("stages.form.fields.successMetric"),
            )}
          </>
        );

      case "Validated":
        return (
          <>
            {renderField(
              "metricsSummary",
              t("stages.form.fields.metricsSummary"),
              "textarea",
            )}
            {renderField(
              "decision",
              t("stages.form.fields.decision"),
              "select",
            )}
            {renderField(
              "rationale",
              t("stages.form.fields.rationale"),
              "textarea",
            )}
          </>
        );

      default:
        return null;
    }
  };

  const currentStage = formData.stage || "Ideation";
  const currentStageIndex = stages.indexOf(currentStage as StageName);

  const handleOverlayClick = (e: MouseEvent<HTMLDivElement>): void => {
    console.log(
      "StageForm: Overlay clicked",
      e.target === e.currentTarget ? "(outside modal)" : "(inside modal)",
      e.target,
    );
    if (e.target === e.currentTarget) {
      console.log("StageForm: Closing modal due to overlay click");
      onCancel();
    }
  };

  const handleOverlayKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      onCancel();
    }
  };

  const handleModalKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    e.stopPropagation();
  };

  return (
    <>
      <div
        className="modal-overlay"
        onClick={handleOverlayClick}
        onKeyDown={handleOverlayKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div
          className="modal wide-modal"
          onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          onKeyDown={handleModalKeyDown}
          role="document"
        >
          <div className="modal-header">
            <h2 id="modal-title">
              {idea ? t("stages.form.titleEdit") : t("stages.form.titleNew")}
            </h2>
            <button className="close-btn" onClick={onCancel}>
              &times;
            </button>
          </div>

          <form onSubmit={handleSubmit} className="modal-form">
            {/* Base fields */}
            <div className="form-section">
              <div
                className="section-header"
                onClick={toggleBasicInfo}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleBasicInfo();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={basicInfoExpanded}
                style={{
                  backgroundColor: basicInfoExpanded ? "#f8f9fa" : "#e3f2fd",
                }}
              >
                <h3 style={{ margin: 0, color: "#333" }}>
                  {t("stages.form.sections.basicInformation")}
                  {!basicInfoExpanded && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#2196f3",
                        marginLeft: "8px",
                        fontWeight: "normal",
                      }}
                    >
                      {t("stages.form.stageLabels.collapsed")}
                    </span>
                  )}
                </h3>
                <span style={{ fontSize: "18px", color: "#666" }}>
                  {basicInfoExpanded ? "\u2212" : "+"}
                </span>
              </div>

              {basicInfoExpanded && (
                <div className="stage-content" style={{ padding: "16px" }}>
                  <div className="form-row">
                    {renderField(
                      "title",
                      t("stages.form.fields.title"),
                      "text",
                      true,
                    )}
                    {renderField(
                      "priority",
                      t("stages.form.fields.priority"),
                      "select",
                    )}
                  </div>
                  {renderField(
                    "description",
                    t("stages.form.fields.description"),
                    "textarea",
                  )}
                  {renderField("tags", t("stages.form.fields.tags"))}
                  {renderField(
                    "stage",
                    t("stages.form.fields.stage"),
                    "select",
                  )}
                </div>
              )}
            </div>

            {/* Stage-specific fields */}
            <div className="stages-container">
              {stages.map((stage, index) => {
                const isExpanded = expandedStages.has(stage);
                const isCurrentStage = stage === currentStage;
                const isPastStage = index < currentStageIndex;
                const isFutureStage = index > currentStageIndex;

                return (
                  <div
                    key={stage}
                    className={`stage-section ${isCurrentStage ? "current-stage" : ""} ${isPastStage ? "past-stage" : ""} ${isFutureStage ? "future-stage" : ""}`}
                  >
                    <div
                      className="section-header"
                      onClick={() => toggleStage(stage)}
                      style={{
                        backgroundColor: isExpanded
                          ? "#f8f9fa"
                          : (STAGE_CONFIGS as StageConfigsMap)[stage]?.color ||
                            "#f0f0f0",
                      }}
                    >
                      <h4 style={{ margin: 0, color: "#333" }}>
                        {stage}{" "}
                        {isCurrentStage && t("stages.form.stageLabels.current")}
                      </h4>
                      <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                        {isExpanded ? "\u2212" : "+"}
                      </span>
                    </div>

                    {isExpanded && (
                      <div
                        className="stage-content"
                        style={{ padding: "16px" }}
                      >
                        {renderStageFields(stage)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Automation Actions Section */}
            <div className="automation-section">
              <div
                className="section-header"
                onClick={toggleAutomation}
                style={{
                  backgroundColor: automationExpanded ? "#f8f9fa" : "#e3f2fd",
                }}
              >
                <div>
                  <h3 style={{ margin: 0, color: "#333" }}>
                    {t("stages.form.sections.automationActions")}
                    {!automationExpanded && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#2196f3",
                          marginLeft: "8px",
                          fontWeight: "normal",
                        }}
                      >
                        {t("stages.form.stageLabels.collapsed")}
                      </span>
                    )}
                  </h3>
                  {automationExpanded && (
                    <p
                      className="section-description"
                      style={{ margin: "8px 0 0 0" }}
                    >
                      {t("stages.form.automation.description")}
                    </p>
                  )}
                </div>
                <span style={{ fontSize: "18px", color: "#666" }}>
                  {automationExpanded ? "\u2212" : "+"}
                </span>
              </div>

              {automationExpanded && (
                <div className="automation-buttons">
                  {(
                    (stageAutomationButtons as StageAutomationButtonsMap)[
                      formData.stage
                    ] || []
                  ).map((action: AutomationAction, index: number) => (
                    <div key={index} className="automation-button-container">
                      <button
                        type="button"
                        className="automation-button"
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          console.log(
                            "StageForm: Automation button clicked directly:",
                            action.label,
                          );
                          e.preventDefault();
                          e.stopPropagation();
                          handleAutomationClick(formData.stage, action.label);
                        }}
                        title={action.description}
                        disabled={
                          action.action === "ai-expand" &&
                          aiLoading["ai-expand"]
                        }
                      >
                        <span className="automation-icon">
                          {action.action === "ai-expand" &&
                          aiLoading["ai-expand"]
                            ? "\u23F3"
                            : action.icon}
                        </span>
                        <div className="automation-content">
                          <span className="automation-label">
                            {action.action === "ai-expand" &&
                            aiLoading["ai-expand"]
                              ? t("stages.form.automation.generatingExpansion")
                              : action.label}
                          </span>
                          <span className="automation-description">
                            {action.description}
                          </span>
                        </div>
                      </button>

                      {/* Sub-tasks if they exist */}
                      {action.subTasks && (
                        <div className="sub-tasks">
                          <div className="sub-tasks-header">
                            <span>{t("stages.form.automation.subTasks")}</span>
                          </div>
                          <div className="sub-tasks-list">
                            {action.subTasks.map(
                              (subTask: SubTask, subIndex: number) => (
                                <div key={subIndex} className="sub-task-item">
                                  <span className="sub-task-label">
                                    {subTask.label}
                                  </span>
                                  <span className="sub-task-description">
                                    {subTask.description}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* AI Expansion Display - moved to a collapsible section */}
            {idea && (
              <div className="form-section">
                <div
                  className="section-header"
                  onClick={() =>
                    setExpandedStages((prev) => {
                      const newSet = new Set(prev);
                      if (newSet.has("ai-expansions")) {
                        newSet.delete("ai-expansions");
                      } else {
                        newSet.add("ai-expansions");
                      }
                      return newSet;
                    })
                  }
                  style={{
                    backgroundColor: expandedStages.has("ai-expansions")
                      ? "#f8f9fa"
                      : "#e8f5e8",
                  }}
                >
                  <div>
                    <h3 style={{ margin: 0, color: "#333" }}>
                      {t("stages.form.sections.aiExpansions")}
                      {!expandedStages.has("ai-expansions") && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#4caf50",
                            marginLeft: "8px",
                            fontWeight: "normal",
                          }}
                        >
                          {t("stages.form.stageLabels.collapsed")}
                        </span>
                      )}
                    </h3>
                    {expandedStages.has("ai-expansions") && (
                      <p
                        className="section-description"
                        style={{ margin: "8px 0 0 0" }}
                      >
                        {t("stages.form.aiExpansions.description")}
                      </p>
                    )}
                  </div>
                  <span style={{ fontSize: "18px", color: "#666" }}>
                    {expandedStages.has("ai-expansions") ? "\u2212" : "+"}
                  </span>
                </div>

                {expandedStages.has("ai-expansions") && (
                  <div
                    style={{ padding: "0" }}
                    onClick={(e: MouseEvent<HTMLDivElement>) =>
                      e.stopPropagation()
                    }
                  >
                    <AIExpansionDisplay
                      ideaId={idea.id}
                      onApprove={(expansionId: number) => {
                        console.log("Expansion approved:", expansionId);
                      }}
                      onReject={(expansionId: number) => {
                        console.log("Expansion rejected:", expansionId);
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="cancel-btn" onClick={onCancel}>
                {t("common:actions.cancel")}
              </button>
              {idea && canDeleteIdea(idea, currentUser) && (
                <button
                  type="button"
                  className="delete-btn"
                  onClick={handleDeleteClick}
                >
                  {t("stages.form.buttons.deleteIdea")}
                </button>
              )}
              <button type="submit" className="submit-btn">
                {idea
                  ? t("stages.form.buttons.updateIdea")
                  : t("stages.form.buttons.createIdea")}
              </button>
            </div>
          </form>

          {/* Delete Confirmation Dialog */}
          {showDeleteConfirm && (
            <div className="modal-overlay" style={{ zIndex: 1001 }}>
              <div
                className="modal"
                style={{ maxWidth: "500px", width: "90%" }}
              >
                <div className="modal-header">
                  <h3>{t("stages.form.deleteConfirm.title")}</h3>
                  <button
                    type="button"
                    className="close-btn"
                    onClick={handleDeleteCancel}
                  >
                    ×
                  </button>
                </div>
                <div style={{ padding: "20px" }}>
                  <p style={{ marginBottom: "16px", color: "#666" }}>
                    {t("stages.form.deleteConfirm.message")}
                  </p>
                  <p
                    style={{
                      fontWeight: "bold",
                      backgroundColor: "#f8f9fa",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      marginBottom: "16px",
                    }}
                  >
                    "{idea?.title}"
                  </p>
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-input"
                      value={deleteConfirmText}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setDeleteConfirmText(e.target.value)
                      }
                      placeholder={t("stages.form.deleteConfirm.placeholder")}
                      style={{ marginBottom: "8px" }}
                    />
                    {deleteError && (
                      <p
                        style={{
                          color: "#dc3545",
                          fontSize: "14px",
                          margin: "8px 0",
                        }}
                      >
                        {deleteError}
                      </p>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                      marginTop: "20px",
                    }}
                  >
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={handleDeleteCancel}
                    >
                      {t("common:actions.cancel")}
                    </button>
                    <button
                      type="button"
                      className="submit-btn"
                      onClick={handleDeleteConfirm}
                      disabled={
                        isDeleting || deleteConfirmText.trim() !== idea?.title
                      }
                      style={{
                        backgroundColor:
                          deleteConfirmText.trim() === idea?.title
                            ? "#dc3545"
                            : "#6c757d",
                        opacity: isDeleting ? 0.6 : 1,
                      }}
                    >
                      {isDeleting
                        ? t("common:actions.deleting")
                        : t("stages.form.buttons.deleteIdea")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: flex-start;
        z-index: 1000;
        padding: 20px;
        overflow-y: auto;
      }

      .modal.wide-modal {
        background: white;
        border-radius: 12px;
        width: 100%;
        max-width: 1200px;
        max-height: 90vh;
        margin: 20px auto;
        display: flex;
        flex-direction: column;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      }

      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 24px 32px;
        border-bottom: 1px solid #e0e0e0;
        background: #f8f9fa;
        border-radius: 12px 12px 0 0;
        flex-shrink: 0;
      }

      .modal-header h2 {
        margin: 0;
        color: #333;
        font-size: 24px;
        font-weight: 600;
      }

      .close-btn {
        background: none;
        border: none;
        font-size: 28px;
        cursor: pointer;
        color: #666;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.2s;
      }

      .close-btn:hover {
        background: #f0f0f0;
        color: #333;
      }

      .modal-form {
        flex: 1;
        overflow-y: auto;
        padding: 32px;
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .form-section {
        background: white;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #e0e0e0;
        margin-bottom: 16px;
        width: 100%;
        box-sizing: border-box;
      }

      .stage-section {
        background: white;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #e0e0e0;
        margin-bottom: 16px;
        width: 100%;
        box-sizing: border-box;
      }

      .automation-section {
        background: white;
        border-radius: 8px;
        overflow: visible;
        border: 1px solid #e0e0e0;
        margin-bottom: 16px;
        width: 100%;
        box-sizing: border-box;
        min-height: auto;
        height: auto;
      }

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        cursor: pointer;
        transition: background-color 0.2s;
        border-bottom: 1px solid #e0e0e0;
        width: 100%;
        box-sizing: border-box;
      }

      .section-header:hover {
        background: #f5f5f5;
      }

      .stage-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        cursor: pointer;
        transition: background-color 0.2s;
        border-bottom: 1px solid #e0e0e0;
        width: 100%;
        box-sizing: border-box;
      }

      .stage-header:hover {
        background: #f5f5f5;
      }

      .section-header h3 {
        margin: 0;
        color: #1976d2;
        font-size: 18px;
        font-weight: 600;
      }

      .section-content {
        padding: 20px;
        background: white;
        width: 100%;
        box-sizing: border-box;
      }

      .stage-content {
        padding: 20px;
        background: white;
        width: 100%;
        box-sizing: border-box;
      }

      .form-group {
        margin-bottom: 20px;
      }

      .form-group label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        color: #333;
        font-size: 14px;
      }

      .form-group input,
      .form-group textarea,
      .form-group select {
        width: 100%;
        padding: 12px 16px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        font-size: 14px;
        transition: border-color 0.2s, box-shadow 0.2s;
        box-sizing: border-box;
      }

      .form-group input:focus,
      .form-group textarea:focus,
      .form-group select:focus {
        outline: none;
        border-color: #2196f3;
        box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
      }

      .form-group textarea {
        min-height: 60px;
        resize: vertical;
        height: auto;
        overflow-y: hidden;
      }

      .error-message {
        color: #f44336;
        font-size: 12px;
        margin-top: 4px;
        display: block;
        font-weight: 500;
      }

      .form-group.has-error input,
      .form-group.has-error textarea,
      .form-group.has-error select {
        border-color: #f44336;
        background-color: #fff5f5;
      }

      .automation-button {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        width: 100%;
        padding: 16px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        background: white;
        cursor: pointer;
        transition: all 0.2s;
        margin-bottom: 0;
        outline: none;
        box-shadow: none;
        text-decoration: none;
        font-family: inherit;
        border-bottom: 2px solid #e0e0e0;
        min-height: auto;
        height: auto;
      }

      .automation-button:hover:not(:disabled) {
        border-color: #2196f3;
        background: #f3f8ff;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15);
      }

      .automation-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .automation-button:focus {
        outline: none;
        border-color: #2196f3;
        box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
      }

      .automation-button:active {
        transform: translateY(0);
        box-shadow: 0 2px 4px rgba(33, 150, 243, 0.1);
      }

      .automation-button-container {
        margin-bottom: 16px;
        width: 100%;
        box-sizing: border-box;
        overflow: visible;
        min-height: auto;
        height: auto;
      }

      .automation-buttons {
        display: flex;
        flex-direction: column;
        gap: 0;
        width: 100%;
        padding: 16px;
        box-sizing: border-box;
        min-height: auto;
        height: auto;
      }

      .automation-icon {
        font-size: 24px;
        flex-shrink: 0;
      }

      .automation-content {
        flex: 1;
        text-align: left;
      }

      .automation-label {
        display: block;
        font-weight: 600;
        color: #333;
        margin-bottom: 4px;
      }

      .automation-description {
        display: block;
        font-size: 13px;
        color: #666;
        line-height: 1.4;
      }

      .sub-tasks {
        margin-top: 8px;
        padding: 12px;
        background: #f8f9fa;
        border-radius: 0 0 8px 8px;
        border: 1px solid #e0e0e0;
        border-top: none;
        width: 100%;
        box-sizing: border-box;
      }

      .sub-tasks-header {
        margin-bottom: 8px;
        font-weight: 600;
        color: #495057;
        font-size: 14px;
      }

      .sub-tasks-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .sub-task-item {
        padding: 8px 12px;
        background: white;
        border-radius: 4px;
        border: 1px solid #dee2e6;
        width: 100%;
        box-sizing: border-box;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }

      .sub-task-label {
        display: block;
        font-weight: 500;
        color: #333;
        font-size: 13px;
        margin-bottom: 2px;
      }

      .sub-task-description {
        display: block;
        font-size: 12px;
        color: #666;
        line-height: 1.3;
      }

      .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding: 24px 32px;
        border-top: 1px solid #e0e0e0;
        background: #f8f9fa;
        flex-shrink: 0;
        border-radius: 0 0 12px 12px;
      }

      .modal-actions button {
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        min-width: 100px;
      }

      .cancel-btn {
        background: #f5f5f5;
        color: #666;
      }

      .cancel-btn:hover {
        background: #e0e0e0;
        color: #333;
      }

      .delete-btn {
        background: #f44336;
        color: white;
      }

      .delete-btn:hover {
        background: #d32f2f;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
      }

      .submit-btn {
        background: #2196f3;
        color: white;
      }

      .submit-btn:hover {
        background: #1976d2;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
      }

      /* Responsive Design */
      @media (max-width: 768px) {
        .modal-overlay {
          padding: 10px;
        }

        .modal.wide-modal {
          max-width: 100%;
          margin: 10px;
          max-height: 95vh;
        }

        .modal-header,
        .modal-form,
        .modal-actions {
          padding: 16px;
        }

        .modal-actions {
          flex-direction: column;
        }

        .modal-actions button {
          width: 100%;
        }
      }

      @media (max-width: 480px) {
        .modal-header h2 {
          font-size: 20px;
        }

        .section-header h3 {
          font-size: 16px;
        }
      }
    `}</style>
    </>
  );
};

export default StageForm;
